import { useEffect, useState } from 'react';
import { AppTable, AppCheckbox, AppButton, AppPagination, AppTag } from '@/components/atoms';
import { PatientEditLink } from '@/components/molecules';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import { SHINSA_STATUS_DISPLAY, type ShinsaStatus } from '@/constants';
import {
  RETURN_SHINSA_COLUMNS,
  formatShinsaDecidedAt,
} from '@/pages/main/rezepts/pages/ReturnInitPage/constants';
import type { ReturnRezeptRow } from '@/services/main/returnRezept/types';
import type { ColumnsType } from 'antd/es/table';

/**
 * BD 8.3 返戻・問合せレセプト登録 — 状態制御.
 * status = 0 (未印刷) → thao tác được. status ≠ 0 (1 = 印刷済み) → khoá.
 * Cùng một điều kiện chi phối CẢ checkbox LẪN button 「再提出年月登録」 của dòng đó,
 * nên để chung một chỗ thay vì lặp lại phép so sánh (HIT-11330).
 */
export const isRowActionable = (row: ReturnRezeptRow): boolean => row.status === 0;
/** 返戻一覧の1ページあたり表示件数（HIT-11331 で 5 から変更）。 */
const PAGE_SIZE = 30;

interface ReturnRezeptTableProps {
  list: ReturnRezeptRow[];
  isLoading: boolean;
  selectedRowKeys: number[];
  onToggleRow: (id: number, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onOpenResubmissionModal: (rezeptId: number) => void;
  /** RS-21 — rezept the mailbox deep link points at; the table turns to its page and highlights it. */
  highlightRezeptId?: number | null;
}

export default function ReturnRezeptTable({
  list,
  isLoading,
  selectedRowKeys,
  onToggleRow,
  onSelectAll,
  onOpenResubmissionModal,
  highlightRezeptId = null,
}: ReturnRezeptTableProps) {
  const navigate = useNavigate();
  const { serviceType } = useTheme();
  const isA2 = serviceType === SERVICE_TYPE_A2;
  const massagerLabel = isA2 ? 'はり・きゅう師' : 'マッサージ師';

  const [currentPage, setCurrentPage] = useState(1);
  // HIT-11331: 1ページの表示件数を 5 → 30 に変更（顧客要望）。
  // BD 8.3 は「common の総件数・ページネーションを使用」としか書いておらず件数を定めていない。
  // 共通定数 DEFAULT_PER_PAGE は 20 で、顧客指定の 30 とは異なるため、この画面固有の値として持つ。
  const pageSize = PAGE_SIZE;

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(list.length / pageSize));
    setCurrentPage(page => Math.min(page, maxPage));
  }, [list.length]);

  // RS-21 — a deep link from the mailbox has to land ON the row, and this table pages PAGE_SIZE rows
  // at a time: without turning to the right page the link would open a screen where the rezept is
  // simply not visible.
  useEffect(() => {
    if (highlightRezeptId === null) return;
    const index = list.findIndex(row => row.id === highlightRezeptId);
    if (index >= 0) setCurrentPage(Math.floor(index / PAGE_SIZE) + 1);
  }, [highlightRezeptId, list]);

  const selectableRows = list.filter(isRowActionable);
  const [headerOverride, setHeaderOverride] = useState(false);
  const allSelectableChecked =
    selectableRows.length > 0
      ? selectableRows.every(r => selectedRowKeys.includes(r.id))
      : headerOverride;

  const columns: ColumnsType<ReturnRezeptRow> = [
    {
      title: (
        <AppCheckbox
          checked={allSelectableChecked}
          onChange={e => {
            setHeaderOverride(e.target.checked);
            onSelectAll(e.target.checked);
          }}
          disabled={list.length === 0}
        />
      ),
      dataIndex: 'id',
      key: 'selection',
      // Just the checkbox: 16px control + the 8px cell padding either side. The 86px this and 受診者番号 and
      // 操作 gave up between them went to マッサージ師 — the TOTAL of the colgroup is unchanged on purpose, so
      // every other column keeps the exact width it renders today (the browser hands out leftover space in
      // proportion to the declared widths, so a same-sum reshuffle leaves the untouched columns alone).
      width: 40,
      align: 'center',
      render: (_: unknown, record: ReturnRezeptRow) => (
        <AppCheckbox
          checked={selectedRowKeys.includes(record.id)}
          disabled={!isRowActionable(record)}
          onChange={e => {
            e.stopPropagation();
            onToggleRow(record.id, e.target.checked);
          }}
        />
      ),
    },
    {
      title: '受診者番号',
      dataIndex: 'serial_number',
      // Down to its own header and nothing more — 「受診者番号」 is 5 full-width characters at the table's 14px
      // font (~70px of ink) plus 8px of cell padding either side; the cell below only ever holds a serial
      // number, so the header is what sizes this column.
      //
      // 36, not 86, because these numbers are SHARES rather than pixels: the table is stretched to its
      // container (scroll.x = true → min-width: 100% under table-layout: fixed, and this page's <main> has no
      // max-width), so the browser hands out the leftover space in proportion to the declared widths and
      // every column renders WIDER than the number written here. The colgroup totals 1052, so the factor is
      // container ÷ 1052 — about 2.3× on the wide desktop this was tuned against, which lands 36 on the
      // header's own ~86px. On a narrower window the factor drops and this header ellipsises: that is the
      // cost of pinning the column to its title, and the reason to change this number is a measurement, not
      // a guess. The pixels it gave up went to 受診者名.
      width: '1%',
      render: (_: unknown, record: ReturnRezeptRow) => (
        <PatientEditLink
          patientId={record.patient_id}
          value={record.serial_number}
          stopPropagation
        />
      ),
    },
    {
      title: '受診者名',
      dataIndex: 'patient_name',
      // Half of what this column and 施術者 held between them (88 + 224 = 312 → 156 each). Both carry free
      // text of the same kind — a 氏名 — so neither has a claim on more of it than the other. The pair's
      // total is unchanged, which is what keeps every other column rendering exactly where it does today.
      width: '156px',
    },
    {
      title: '保険者番号',
      dataIndex: 'insurer_number',
      width: '80px',
    },
    {
      title: '施術年月',
      dataIndex: 'treatement_yearmonth_jp',
      width: '78px',
    },
    {
      // HIT-11329: BD 8.3 返戻・問合せレセプト登録 項目12 — 施術年月 と 本・関 の間。
      // 「再提出年月一括登録」で登録した月をここで確認する列。未登録なら空欄
      // (ReturnRezeptResource が rezept_yearmonth = null のとき '' を返す)。
      title: '再提出年月',
      dataIndex: 'rezept_yearmonth_jp',
      // Same value format as 施術年月 (Treatment month), so the same share. A width-less column takes the
      // table's whole leftover space under enableEllipsis (see 本・関 (Main/Related) below).
      width: '78px',
    },
    {
      // WIDTH IS LOAD-BEARING. This was the ONLY column on the screen without one, and antd hands every
      // width-less column the table's entire leftover space under table-layout: fixed -- so a cell holding a
      // SINGLE character (本 or 関) rendered ~500px wide while its neighbours were squeezed. Nothing about the
      // data caused it; the empty-result view was just as wide.
      //
      // 52px matches 同意書, the other three-full-width-character header on this table, and the header text is
      // the real floor here because AppTable pins header cells to white-space: nowrap.
      //
      // STRING, not number: AppTable deletes NUMERIC widths >= 80 under enableEllipsis (AppTable.tsx:116), so
      // every data column here states its width as a string on purpose. 52 would survive as a number today,
      // but stating it the same way as its neighbours keeps the rule from being re-learned the hard way.
      title: '本・関',
      dataIndex: 'rezept_kbn',
      width: '52px',
      align: 'center',
      render: (kbn: number) => (kbn === 2 ? '関' : '本'),
    },
    {
      title: '同意書',
      dataIndex: 'consent_serial_number',
      width: '52px',
    },
    {
      title: '保険証・訪問変更',
      dataIndex: 'is_locked',
      width: '1%',
      render: (locked: number) => (locked === 0 ? '可' : '不可'),
    },
    {
      title: massagerLabel,
      dataIndex: 'massager_name',
      // Half of what this column and 受診者名 held between them (224 + 88 = 312 → 156 each): 224 left this one
      // wide enough to show a whole 施術者名 while its neighbour clipped a 氏名 at three characters. The pair's
      // total is unchanged, which is what keeps every other column rendering exactly where it does today.
      width: '156px',
    },
    // RS-21 — Shinsa's side of the story, on the screen where the clinic acts on it. Placed together and
    // last-but-one so the existing layout above is untouched: everything a clinic already reads stays where
    // it was, and the new block reads as one thought.
    {
      title: RETURN_SHINSA_COLUMNS.STATUS,
      dataIndex: 'shinsa_status',
      width: '1%',
      align: 'center',
      // Blank when nothing was heard from Shinsa — NOT 未送信 (RS-10).
      //
      // 🔴 Q1 — 一部不支給 DOES reach this screen in one of the two cases, so the tag below has to RENDER it,
      // not merely survive it. An earlier version of this comment claimed the opposite ("cannot reach this
      // screen") and had the reasoning backwards.
      //
      // Receiving RET_KBN 9 writes the `shinsa_status` badge and nothing else — not `status`, not
      // `is_return` (owner's decision 2026-08-03; backend/app/Services/Sync/Inbound/RezeptStatusHandler.php,
      // withdrawsReturn()). This list is `is_return = 1 AND status <> 確定`
      // (backend/app/Repositories/Clinic/RezeptRepository.php:1445-1449, the `// 返戻` branch inside
      // addWhereFilters — re-read 2026-08-03). Writing nothing therefore cuts BOTH ways:
      //   · a rezept that was NEVER returned still has is_return = 0 and stays off this screen;
      //   · a rezept ALREADY returned when the 9 arrives keeps is_return = 1 and STAYS here, wearing this
      //     badge. RAH does not un-return it — the accepted cost of the badge-only decision, not a bug.
      //
      // So the label has to FIT here, not be abbreviated away: the column was 62px and rendered 一部不支給 as
      // 「一部」, which on this screen is worse than on 一覧 — this is the screen a clinic opens to decide
      // what to resubmit, and 「一部」 does not say 「do not resubmit this one」. Widened to the same 104px
      // 一覧 uses (RezeptListTable.tsx), so both screens spell the verdict out identically. The string width
      // is deliberate — AppTable only strips NUMERIC widths >= 80 under enableEllipsis (AppTable.tsx:116),
      // so '104px' survives.
      render: (value: string | null) => {
        if (!value) return null;
        const display = SHINSA_STATUS_DISPLAY[value as ShinsaStatus];
        return (
          <AppTag color={display?.color ?? 'default'} className="m-0">
            {value}
          </AppTag>
        );
      },
    },
    {
      title: RETURN_SHINSA_COLUMNS.RETURN_TYPE,
      dataIndex: 'shinsa_return_type_label',
      width: '66px',
      align: 'center',
    },
    {
      title: RETURN_SHINSA_COLUMNS.DECIDED_AT,
      // The META's 返戻日, not shinsa_status_at — that column became 「when the badge moved」 on 2026-08-05
      // and would show the delivery timestamp under a header reading 返戻日.
      dataIndex: 'shinsa_return_date',
      // Takes every pixel 受診者番号 gave up. It needs them: the value is 和暦 now (「令和08年08月11日」, 8
      // full-width characters) where it used to be 「2026/08/11」.
      width: '94px',
      align: 'center',
      render: (value: string | null) => formatShinsaDecidedAt(value),
    },
    {
      title: '操作',
      key: 'action',
      align: 'center',
      // String width on purpose: AppTable strips NUMERIC widths >= 80 under enableEllipsis so the data
      // columns auto-distribute, but this column holds three buttons and must keep its space. Measured
      // before that fix: it had collapsed to 75px and the buttons were unusable.
      //
      // Sized to the LONGEST BUTTON and nothing more. 「再提出年月登録」 is 7 full-width characters (98px of ink
      // at 14px) inside an antd small button (7px padding either side + 1px border) inside a cell (8px
      // padding either side) = 130px of real width; the other two labels are shorter and stretch to match.
      // 88 is that 130 divided by the ~1.5× the stretch applies (see 受診者番号 above) — these widths are
      // shares, not pixels. Everything it gave up went to マッサージ師.
      width: '88px',
      render: (_: unknown, record: ReturnRezeptRow) => {
        // HIT-3536: 施術入力 → /visit_clinic/edit/{patient_id}/{wareki_yearmonth}
        // 訪問入力 → /visits/schedule/{massager_id}/{first_visit_date} (week of first visit).
        // Fallback to /visits/schedule if data missing so the button still navigates.
        const visitClinicPath = `../visit_clinic/edit/${record.patient_id}/${encodeURIComponent(record.treatement_yearmonth_jp)}`;
        const visitSchedulePath =
          record.massager_id && record.first_visit_date
            ? `../visits/schedule/${record.massager_id}/${record.first_visit_date}`
            : `../visits/schedule`;
        // Stacked, not a 2-column grid: this screen carries 13 columns and the 操作 column is only as wide as
        // one button. A 2-up grid halved that again and clipped every label. One button per line uses the
        // width it has — no max-width, because the column is now sized to the longest label itself.
        return (
          <div className="mx-auto flex w-full flex-col gap-1">
            <AppButton
              type="primary"
              size="small"
              className="w-full"
              // HIT-11330: BD「状態制御」— checkbox 無効時（status = 1 / 印刷済み）は
              // 「再提出年月登録」ボタンも無効化する。dòng đã in rồi thì không được
              // đăng ký lại 再提出年月 qua nút của dòng (luồng 一括登録 vốn đã lọc status = 0).
              disabled={!isRowActionable(record)}
              onClick={e => {
                e.stopPropagation();
                onOpenResubmissionModal(record.id);
              }}
            >
              再提出年月登録
            </AppButton>
            <AppButton
              size="small"
              className="w-full"
              onClick={e => {
                e.stopPropagation();
                navigate(visitClinicPath);
              }}
            >
              施術入力
            </AppButton>
            <AppButton
              size="small"
              className="w-full"
              onClick={e => {
                e.stopPropagation();
                navigate(visitSchedulePath);
              }}
            >
              訪問入力
            </AppButton>
          </div>
        );
      },
    },
  ];

  const totalCount = list.length;
  const totalCountLabel = `該当件数: ${totalCount} 件`;

  return (
    <>
      <div className="mb-2 text-sm text-gray-700">{totalCountLabel}</div>
      <AppTable<ReturnRezeptRow>
        columns={columns}
        dataSource={list.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
        rowKey="id"
        loading={isLoading}
        bordered
        pagination={false}
        // Fit the container and ellipsis the overflow (AppTable contract) — the three RS-21 columns pushed
        // the natural width past 960px and squeezed 操作 to 75px.
        enableEllipsis
        // The highlight rule lives in scss/index.scss and targets the CELLS: antd gives .ant-table-cell its
        // own background, so a Tailwind class on the <tr> renders invisible (verified in a browser).
        rowClassName={row => (row.id === highlightRezeptId ? 'deep-link-row' : '')}
      />
      <AppPagination
        className="mt-2"
        current={currentPage}
        total={list.length}
        pageSize={pageSize}
        onChange={setCurrentPage}
      />
    </>
  );
}
